import { useEffect, useState } from 'react';
import { Table, Button, toaster, Pane, Text, Card, Tooltip, IconButton, Heading } from 'evergreen-ui';
import { getGuests, deleteGuest, fetchGuestAttachments } from '../services/guestsServices';
import { useRole } from '../contexts/RoleContext';
import { ATTACHMENT_CATEGORIES } from '../constants/guestAttachments';
import { RemoveIcon, EditIcon, MoreIcon } from 'evergreen-ui';
import EditGuestDialog from './EditGuestDialog';
import GuestViewDialog from './GuestViewDialog';
import useWindowSize from '../utils/useWindowSize';
import { getExtraFieldsForGuest } from '../services/guestExtraFieldsService';
import '../styles/responsive.css';

const GuestsTable = ({
    guests,
    setGuests,
    editingGuestIndex,
    setEditingGuestIndex,
    isEditing,
    isDraggable,
    onGuestDragStart,
    onGuestDragEnd,
}) => {
    const { effectiveIsDirector: isDirector } = useRole();
    const [isEditDialogShown, setIsEditDialogShown] = useState(false);
    const [isViewDialogShown, setIsViewDialogShown] = useState(false);
    const [viewingGuest, setViewingGuest] = useState(null);
    const [expandedGuestId, setExpandedGuestId] = useState(null);
    const [guestAttachmentsMap, setGuestAttachmentsMap] = useState({});
    const [guestExtraFieldsMap, setGuestExtraFieldsMap] = useState({});
    const width = useWindowSize();
    const isMobile = width < 920;
    const isNarrow = width < 1650;

    useEffect(() => {
        const fetchDataForExpanded = async () => {
            if (!expandedGuestId) return;
            try {
                const [atts, extraFields] = await Promise.all([
                    fetchGuestAttachments(expandedGuestId),
                    getExtraFieldsForGuest(expandedGuestId),
                ]);
                setGuestAttachmentsMap((m) => ({ ...m, [expandedGuestId]: atts }));
                setGuestExtraFieldsMap((m) => ({ ...m, [expandedGuestId]: extraFields || [] }));
            } catch (err) {
                console.error('Errore fetch dati ospite espanso:', err);
            }
        };
        fetchDataForExpanded();
    }, [expandedGuestId]);

    const handleEditClick = (index) => {
        setEditingGuestIndex(index);
        setIsEditDialogShown(true);
    };

    const handleCloseEditDialog = () => {
        setIsEditDialogShown(false);
        setEditingGuestIndex(null);
    };

    const handleRemoveClick = async (guestId) => {
        if (!window.confirm('Sei sicuro di voler rimuovere questo ospite?')) return;
        try {
            const result = await deleteGuest(guestId);
            if (result && (result.success === true || result.deleted === true)) {
                toaster.success('Ospite rimosso con successo.');
                setGuests(prev => prev.filter((g) => g.id !== guestId));
            } else {
                toaster.danger('Errore durante la rimozione dell\'ospite.');
            }
        } catch (error) {
            console.error('Errore durante la rimozione dell\'ospite:', error);
            toaster.danger('Errore durante la rimozione dell\'ospite.');
        }
    };

    const handleToggleExpand = (guestId) => {
        setExpandedGuestId((prev) => (prev === guestId ? null : guestId));
    };
    
    if (isMobile) {
        return (
            <>
                <div className="responsive-list">
                    {Array.isArray(guests) && guests.length > 0 ? (
                        guests.map((guest, index) => (
                            <Card
                                key={`card-${guest.id}`}
                                className="responsive-card"
                                draggable={isDraggable}
                                onDragStart={isDraggable ? (e) => onGuestDragStart?.(e, guest) : undefined}
                                onDragEnd={isDraggable ? onGuestDragEnd : undefined}
                                style={isDraggable ? { cursor: 'grab' } : undefined}
                            >
                                <div className="field-row">
                                    <div className="field-label">Nome</div>
                                    <div className="field-value">{guest.nome}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Cognome</div>
                                    <div className="field-value">{guest.cognome}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Data di nascita</div>
                                    <div className="field-value">{guest.data_nascita ? guest.data_nascita.split('-').reverse().join('/') : ''}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Sesso</div>
                                    <div className="field-value">{guest.sesso}</div>
                                </div>
                                <div className="field-row">
                                    <div className="field-label">Motivazione</div>
                                    <div className="field-value">{guest.motivazione}</div>
                                </div>

                                <div className="field-row">
                                    <div className="field-label">PEI</div>
                                    <div className="field-value">
                                        {guest.revisione_pei_url ? (
                                            <Text cursor="pointer" onClick={() => window.open(guest.revisione_pei_url, '_blank')}>{guest.revisione_pei_url.split('/').pop()}</Text>
                                        ) : (
                                            <Text>Nessun PEI</Text>
                                        )}
                                    </div>
                                </div>

                                <div className="actions-row">
                                    {isEditing ? (
                                        <>
                                            <Button className="action-btn" appearance="primary" intent="success" onClick={() => { handleEditClick(index); }}>
                                                Modifica <EditIcon marginLeft={8} />
                                            </Button>
                                            <Button className="action-btn" intent="danger" onClick={() => handleRemoveClick(guest.id)}>
                                                Rimuovi <RemoveIcon marginLeft={8} />
                                            </Button>
                                        </>
                                    ) : (
                                        <Button className="action-btn" onClick={() => { setViewingGuest(guest); setIsViewDialogShown(true); }}>
                                            Dettagli <MoreIcon marginLeft={8} />
                                        </Button>
                                    )}
                                </div>
                            </Card>
                        ))
                    ) : (
                        <Card className="responsive-card">Nessun ospite disponibile.</Card>
                    )}
                </div>

                <EditGuestDialog
                    isShown={isEditDialogShown}
                    onClose={handleCloseEditDialog}
                    guest={editingGuestIndex !== null ? guests[editingGuestIndex] : null}
                    fetchGuests={async () => {
                        const guestsData = await getGuests();
                        setGuests(guestsData);
                    }}
                />
                
                <GuestViewDialog
                    isShown={isViewDialogShown}
                    onClose={() => { setIsViewDialogShown(false); setViewingGuest(null); }}
                    guest={viewingGuest}
                />
            </>
        );
    }

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.TextHeaderCell>Nome</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Cognome</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Sesso</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Data di Nascita</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Luogo di Nascita</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Residenza</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Domicilio</Table.TextHeaderCell>
                    <Table.TextHeaderCell>Motivazione</Table.TextHeaderCell>
                    {isEditing && (
                        <Table.TextHeaderCell>Azioni</Table.TextHeaderCell>
                    )}
                </Table.Head>
                <Table.Body>
                    {Array.isArray(guests) && guests.length > 0 ? (
                        guests.map((guest, index) => {
                            return [
                                <Table.Row
                                    key={guest.id}
                                    isSelectable
                                    onSelect={() => handleToggleExpand(guest.id)}
                                    paddingY={8}
                                    minHeight={48}
                                    height="auto"
                                    draggable={isDraggable}
                                    onDragStart={isDraggable ? (e) => { e.stopPropagation(); onGuestDragStart?.(e, guest); } : undefined}
                                    onDragEnd={isDraggable ? onGuestDragEnd : undefined}
                                    style={isDraggable ? { cursor: 'grab' } : undefined}
                                >
                                    <Table.TextCell>{guest.nome}</Table.TextCell>
                                    <Table.TextCell>{guest.cognome}</Table.TextCell>
                                    <Table.TextCell>{guest.sesso}</Table.TextCell>
                                    <Table.TextCell>{guest.data_nascita ? guest.data_nascita.split('-').reverse().join('/') : ''}</Table.TextCell>
                                    <Table.TextCell>{guest.luogo_nascita}</Table.TextCell>
                                    <Table.TextCell>{guest.residenza}</Table.TextCell>
                                    <Table.TextCell>{guest.domicilio}</Table.TextCell>
                                    <Table.TextCell>{guest.motivazione}</Table.TextCell>
                                    {isEditing && (
                                        <Table.TextCell style={{ whiteSpace: 'normal' }}>
                                            <div style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center', minWidth: 140, flexWrap: 'wrap' }}>
                                                {isNarrow ? (
                                                    <>
                                                        <Tooltip content="Modifica">
                                                            <IconButton icon={EditIcon} intent='none' onClick={(e) => { e.stopPropagation(); handleEditClick(index); }} aria-label="Modifica" />
                                                        </Tooltip>
                                                        <Tooltip content="Rimuovi">
                                                            <IconButton icon={RemoveIcon} intent="danger" onClick={(e) => { e.stopPropagation(); handleRemoveClick(guest.id); }} aria-label="Rimuovi" />
                                                        </Tooltip>
                                                    </>
                                                ) : (
                                                    <Pane display="flex" flexDirection="column" gap={8} alignItems="flex-start">
                                                        <Button
                                                            appearance='primary'
                                                            intent="success"
                                                            onClick={(e) => { e.stopPropagation(); handleEditClick(index); }}
                                                        >
                                                            Modifica <EditIcon marginLeft={8} />
                                                        </Button>
                                                        <Button
                                                            intent="danger"
                                                            onClick={(e) => { e.stopPropagation(); handleRemoveClick(guest.id); }}
                                                        >
                                                            Rimuovi <RemoveIcon marginLeft={8} />
                                                        </Button>
                                                    </Pane>
                                                )}
                                            </div>
                                        </Table.TextCell>
                                    )}
                                </Table.Row>,
                                expandedGuestId === guest.id ? (
                                    <Table.Row key={`expanded-${guest.id}`} height="auto" paddingY={8}>
                                        <Table.Cell colSpan={isEditing ? 9 : 8}>
                                            <Pane display="flex" flexDirection="column" gap={16} padding={8} width="100%">

                                                {/* Contatti ospite */}
                                                {(guest.iban || guest.telefono || guest.email) && (
                                                    <Pane>
                                                        <Heading size={300} marginBottom={4}>Contatti ospite</Heading>
                                                        {guest.iban && <Text display="block"><strong>IBAN:</strong> {guest.iban}</Text>}
                                                        {guest.telefono && <Text display="block"><strong>Telefono:</strong> {guest.telefono}</Text>}
                                                        {guest.email && <Text display="block"><strong>Email:</strong> {guest.email}</Text>}
                                                    </Pane>
                                                )}

                                                {/* Riferimenti professionali e familiari */}
                                                {[
                                                    { label: 'Avvocato', prefix: 'avv' },
                                                    { label: 'Assistente sociale', prefix: 'ass' },
                                                    { label: 'Specialista di riferimento', prefix: 'spec' },
                                                    { label: 'SerD', prefix: 'serd' },
                                                    { label: 'Madre', prefix: 'madre' },
                                                    { label: 'Padre', prefix: 'padre' },
                                                ].map(({ label, prefix }) => {
                                                    const nome = guest[`${prefix}_nome`];
                                                    const cognome = guest[`${prefix}_cognome`];
                                                    const mail = guest[`${prefix}_mail`];
                                                    const tel = guest[`${prefix}_telefono`];
                                                    const ind = guest[`${prefix}_indirizzo`];
                                                    if (!nome && !cognome && !mail && !tel && !ind) return null;
                                                    return (
                                                        <Pane key={prefix}>
                                                            <Heading size={300} marginBottom={4}>{label}</Heading>
                                                            {(nome || cognome) && <Text display="block"><strong>Nome:</strong> {[nome, cognome].filter(Boolean).join(' ')}</Text>}
                                                            {tel && <Text display="block"><strong>Tel:</strong> {tel}</Text>}
                                                            {mail && <Text display="block"><strong>Email:</strong> {mail}</Text>}
                                                            {ind && <Text display="block"><strong>Indirizzo:</strong> {ind}</Text>}
                                                        </Pane>
                                                    );
                                                })}

                                                {/* PEI e Allegati */}
                                                <Pane display="flex" gap={24} flexWrap="wrap">
                                                    <Pane flex={1} minWidth={200}>
                                                        <Text fontWeight={600}>PEI</Text>
                                                        <Pane marginTop={4}>
                                                            {guest.revisione_pei_url ? (
                                                                <Text cursor="pointer" onClick={() => window.open(guest.revisione_pei_url, '_blank')}>
                                                                    {guest.revisione_pei_url.split('/').pop()}
                                                                </Text>
                                                            ) : (
                                                                <Text color="muted">Nessun PEI caricato.</Text>
                                                            )}
                                                        </Pane>
                                                    </Pane>
                                                    <Pane flex={2} minWidth={200}>
                                                        <Text fontWeight={600}>Allegati</Text>
                                                        {(() => {
                                                            const atts = guestAttachmentsMap[guest.id] || [];
                                                            const hasAny = atts.length > 0;
                                                            if (!hasAny) return <Text color="muted" display="block" marginTop={4}>Nessun allegato.</Text>;
                                                            return (
                                                                <Pane marginTop={4} display="flex" flexDirection="column" gap={8}>
                                                                    {ATTACHMENT_CATEGORIES.map(cat => {
                                                                        const items = atts.filter(a => a.category === cat);
                                                                        if (items.length === 0) return null;
                                                                        return (
                                                                            <Pane key={cat}>
                                                                                <Text size={300} fontWeight={600} textTransform="capitalize">{cat}</Text>
                                                                                <Pane display="flex" flexDirection="column" gap={2} marginTop={2}>
                                                                                    {items.map(att => (
                                                                                        <Text key={att.id} cursor="pointer" onClick={() => window.open(att.file_url, '_blank')}>
                                                                                            {att.file_name}
                                                                                        </Text>
                                                                                    ))}
                                                                                </Pane>
                                                                            </Pane>
                                                                        );
                                                                    })}
                                                                    {atts.filter(a => !a.category).length > 0 && (
                                                                        <Pane>
                                                                            <Text size={300} fontWeight={600}>Senza categoria</Text>
                                                                            <Pane display="flex" flexDirection="column" gap={2} marginTop={2}>
                                                                                {atts.filter(a => !a.category).map(att => (
                                                                                    <Text key={att.id} cursor="pointer" onClick={() => window.open(att.file_url, '_blank')}>
                                                                                        {att.file_name}
                                                                                    </Text>
                                                                                ))}
                                                                            </Pane>
                                                                        </Pane>
                                                                    )}
                                                                </Pane>
                                                            );
                                                        })()}
                                                    </Pane>
                                                </Pane>

                                            {/* Campi personalizzati */}
                                            {(() => {
                                                const extras = (guestExtraFieldsMap[guest.id] || []).filter(f => f.field_value);
                                                if (extras.length === 0) return null;
                                                return (
                                                    <Pane>
                                                        <Heading size={300} marginBottom={4}>Campi personalizzati</Heading>
                                                        {extras.map(f => (
                                                            <Text key={f.field_name} display="block">
                                                                <strong>{f.field_name}:</strong> {f.field_value}
                                                            </Text>
                                                        ))}
                                                    </Pane>
                                                );
                                            })()}

                                            </Pane>
                                        </Table.Cell>
                                    </Table.Row>
                                ) : null
                            ];
                        })
                    ) : (
                        <Table.Row>
                            <Table.TextCell>Nessun ospite disponibile.</Table.TextCell>
                        </Table.Row>
                    )}
                </Table.Body>
            </Table>

            <EditGuestDialog
                isShown={isEditDialogShown}
                onClose={handleCloseEditDialog}
                guest={editingGuestIndex !== null ? guests[editingGuestIndex] : null}
                fetchGuests={async () => {
                    const guestsData = await getGuests();
                    setGuests(guestsData);
                }}
            />
        </>
    );
};

export default GuestsTable;